import axios from "axios";
import {
  BASEURL,
  S3BUCKETURL,
  APIURL,
  LENDERURL,
  environment,
  APIGATEWAYBASEURL,
  PLPRICINGAPIGATEWAYBASEURL,
  FEATURESWITCHBASEURL,
} from "../../environments/environment";
import { Token } from "../after-login/token/token";
import { TokenService } from "./TokenService";
import { LoginService } from "./LoginService";
import { USE_MOCK_API, getMockResponse } from "../utils/mockData";
import { createLogger } from "./Logger";

const log = createLogger('BackendService');

// In development, use proxy to avoid CORS issues
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

// Proxy URLs for development (see src/setupProxy.js)
const DEV_S3_BUCKET_PROXY = "/s3-bucket";
const DEV_API_PROXY = "/app-proxy";

export class BackendService {
  private tokenService: TokenService;
  private loginService: LoginService;

  constructor(tokenService: TokenService, loginService: LoginService) {
    this.tokenService = tokenService;
    this.loginService = loginService;
  }

  checkParallelSessions = () => {
    const localToken = this.tokenService.getLocalCustomerId();
    const sessionToken = this.tokenService.getCustomerId();
    if (localToken && sessionToken && localToken !== sessionToken) {
      this.tokenService.removeAll();
      this.loginService.goToLogoutUrl();
    }
  };

  setHeaders(
    token: Token | null | undefined,
    isBucket: boolean = false,
  ): Record<string, string> {
    this.checkParallelSessions();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    };

    if (token && !isBucket) {
      headers["Authorization"] = `${token.token_type} ${token.access_token}`;
    }

    return headers;
  }

  async setData(url: string, postParams: Record<string, unknown> = {}): Promise<unknown> {
    // Return mock data if mock mode is enabled
    if (USE_MOCK_API) {
      const mockData = getMockResponse(url);
      if (mockData !== null) {
        log.debug(`MOCK POST: ${url}`, postParams);
        return Promise.resolve(mockData);
      }
      log.debug(`MOCK POST (no data): ${url}`, postParams);
      return Promise.resolve({ success: true });
    }

    const token = this.tokenService.getToken();
    const urlWithCustomerId = url.replace(
      "{{customerId}}",
      this.tokenService.getCustomerId() || "",
    );

    // In development, use proxy to avoid CORS; in production, use direct URL
    const requestUrl = IS_DEVELOPMENT
      ? DEV_API_PROXY + urlWithCustomerId
      : BASEURL + urlWithCustomerId;

    log.info(`POST: ${requestUrl}`, postParams);

    try {
      const response = await axios.post(requestUrl, postParams, {
        headers: this.setHeaders(token),
      });
      return response.data;
    } catch (err: unknown) {
      log.error(`POST failed: ${requestUrl}`, err instanceof Error ? err.message : String(err));
      throw err;
    }
  }

  async getData(
    url: string = "",
    isRelative: boolean = false,
    skipToken: boolean = false,
    isBucket: boolean = false,
    getParams: Record<string, string | number | boolean> = {},
  ): Promise<unknown> {
    // Return mock data if mock mode is enabled
    if (USE_MOCK_API) {
      const mockData = getMockResponse(url);
      if (mockData !== null) {
        return Promise.resolve(mockData);
      }
    }

    const token = this.tokenService.getToken();
    let requestUrl = url.replace(
      "{{customerId}}",
      this.tokenService.getCustomerId() || "",
    );

    if (isBucket) {
      // In development, use proxy to avoid CORS; in production, use S3 URL directly
      requestUrl = IS_DEVELOPMENT
        ? DEV_S3_BUCKET_PROXY + requestUrl
        : S3BUCKETURL + requestUrl;
    } else if (!isRelative) {
      // In development, use proxy to avoid CORS; in production, use direct URL
      requestUrl = IS_DEVELOPMENT
        ? DEV_API_PROXY + requestUrl
        : BASEURL + requestUrl;
    }

    log.info(`GET: ${requestUrl}`);

    try {
      const response = await axios.get(requestUrl, {
        headers: this.setHeaders(token, isBucket),
        params: getParams,
      });
      return response.data;
    } catch (err: unknown) {
      log.error(`GET failed: ${requestUrl}`, err instanceof Error ? err.message : String(err));
      // Previously: 401 → removeOnlyToken() + goToLoginUrl()
      // This was too aggressive: a single non-critical endpoint returning 401
      // (e.g. zestlife-channel/redemption) wiped a freshly-obtained valid token
      // and kicked the user out of /dashboard immediately after login.
      // Now: just re-throw (matching setData, putData, and all other methods).
      // Callers handle 401 themselves; AuthGuard catches truly expired tokens.
      throw err;
    }
  }

  async putData(url: string, postParams: Record<string, unknown> = {}): Promise<unknown> {
    const token = this.tokenService.getToken();
    const urlWithCustomerId = url.replace(
      "{{customerId}}",
      this.tokenService.getCustomerId() || "",
    );

    // In development, use proxy to avoid CORS; in production, use direct URL
    const requestUrl = IS_DEVELOPMENT
      ? DEV_API_PROXY + urlWithCustomerId
      : BASEURL + urlWithCustomerId;

    log.info(`PUT: ${requestUrl}`, postParams);

    try {
      const response = await axios.put(requestUrl, postParams, {
        headers: this.setHeaders(token),
      });
      return response.data;
    } catch (err: unknown) {
      log.error(`PUT failed: ${requestUrl}`, err instanceof Error ? err.message : String(err));
      throw err;
    }
  }

  async putDataToS3(url: string, postParams: Blob | Record<string, unknown>): Promise<unknown> {
    const requestUrl = url.replace(
      "{{customerId}}",
      this.tokenService.getCustomerId() || "",
    );

    try {
      const response = await axios.put(requestUrl, postParams);
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }

  setHeadersForAPIGateway(
    apiKey?: string,
    token?: Token | null,
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "x-api-key": apiKey ? apiKey : environment.featureSwtichApi,
    };

    if (token) {
      headers["Authorization"] = `${token.token_type} ${token.access_token}`;
    }

    return headers;
  }

  async getCallForAPIGateway(
    url: string,
    PLPricingUpdate: boolean = false,
  ): Promise<unknown> {
    // Return mock data if mock mode is enabled
    if (USE_MOCK_API) {
      const mockData = getMockResponse(url);
      if (mockData !== null) {
        return Promise.resolve(mockData);
      }
    }

    this.tokenService.getToken(); // ensure token is initialized

    url = url.replace(
      "{{customerId}}",
      this.tokenService.getCustomerId() || "",
    );
    url =
      (PLPricingUpdate ? PLPRICINGAPIGATEWAYBASEURL : APIGATEWAYBASEURL) + url;

    try {
      const response = await axios.get(url, {
        headers: this.setHeadersForAPIGateway(),
      });
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }

  async postCallForAPIGateway(
    url: string,
    postParams: Record<string, unknown> = {},
    PLPricingUpdate: boolean = false,
  ): Promise<unknown> {
    this.tokenService.getToken(); // ensure token is initialized

    url = url.replace(
      "{{customerId}}",
      this.tokenService.getCustomerId() || "",
    );
    url =
      (PLPricingUpdate ? PLPRICINGAPIGATEWAYBASEURL : APIGATEWAYBASEURL) + url;

    try {
      const response = await axios.post(url, postParams, {
        headers: this.setHeadersForAPIGateway(),
      });
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }

  async getCallForAPI(url: string, apiKey: string): Promise<unknown> {
    this.tokenService.getToken(); // ensure token is initialized
    url =
      APIURL +
      url.replace("{{customerId}}", this.tokenService.getCustomerId() || "");

    try {
      const response = await axios.get(url, {
        headers: this.setHeadersForAPIGateway(apiKey),
      });
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }

  async postCallForAPI(
    url: string,
    postParams: Record<string, unknown> = {},
    apiKey: string,
  ): Promise<unknown> {
    const token = this.tokenService.getToken();
    url = APIURL + url;

    try {
      const response = await axios.post(url, postParams, {
        headers: this.setHeadersForAPIGateway(apiKey, token || null),
      });
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }

  async postCallForFeaturesApi(
    url: string,
    postParams: Record<string, unknown> = {},
    apiKey = environment.featuresApiKey,
  ): Promise<unknown> {
    try {
      const response = await axios.post(url, postParams, {
        headers: this.setHeadersForAPIGateway(apiKey),
      });
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }

  async getCallForFeaturesApi(url: string): Promise<unknown> {
    url = FEATURESWITCHBASEURL + url;

    try {
      const response = await axios.get(url, {
        headers: this.setHeadersForAPIGateway(environment.featuresApiKey),
      });
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }

  async getCallForLender(url: string): Promise<unknown> {
    const token = this.tokenService.getToken();
    url = LENDERURL + url;

    try {
      const response = await axios.get(url, {
        headers: this.setHeaders(token),
      });
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }

  async putDataForConsent(url: string, postParams: Record<string, unknown> = {}): Promise<unknown> {
    const token = this.tokenService.getToken();
    const requestUrl =
      BASEURL +
      url.replace("{{customerId}}", this.tokenService.getCustomerId() || "");

    try {
      const response = await axios.put(requestUrl, postParams, {
        headers: this.setHeaders(token),
      });
      return response.data;
    } catch (err: unknown) {
      throw err;
    }
  }
}
